import { Bell, Globe, LogOut, Moon, ShoppingCart, Sun, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/shared/auth/auth-store';
import { useLocale } from '@/shared/i18n/useLocale';
import { formatDateTime } from '@/shared/lib/format';

export function Topbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { locale, change } = useLocale();
  const [now, setNow] = useState(new Date());
  const [dark, setDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const toggleDark = () => {
    const next = !dark;
    document.documentElement.classList.toggle('dark', next);
    setDark(next);
    try {
      localStorage.setItem('elshahd_theme', next ? 'dark' : 'light');
    } catch {
      /* ignore */
    }
  };

  const initials = (user?.username ?? user?.email ?? 'U')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/85 px-6 backdrop-blur">
      <div className="flex-1" />

      {/* live clock */}
      <div className="hidden text-sm text-muted-foreground md:block tabular-nums">
        {formatDateTime(now, locale === 'ar' ? 'ar-EG' : 'en-US')}
      </div>

      {/* POS quick-launch */}
      <Button
        onClick={() => navigate('/app/pos')}
        size="sm"
        className="hidden h-9 gap-1.5 sm:inline-flex"
      >
        <ShoppingCart className="h-4 w-4" />
        {t('nav.pos', 'POS')}
      </Button>
      <Button
        onClick={() => navigate('/app/pos')}
        size="icon"
        className="sm:hidden"
        aria-label={t('nav.pos', 'POS')}
      >
        <ShoppingCart className="h-4 w-4" />
      </Button>

      {/* language */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t('common.language', 'Language')}>
            <Globe className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('common.language', 'Language')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => void change('ar')}>العربية</DropdownMenuItem>
          <DropdownMenuItem onClick={() => void change('en')}>English</DropdownMenuItem>
          <DropdownMenuItem onClick={() => void change('fr')}>Français</DropdownMenuItem>
          <DropdownMenuItem onClick={() => void change('es')}>Español</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* theme */}
      <Button variant="ghost" size="icon" onClick={toggleDark} aria-label="Toggle theme">
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      {/* notifications */}
      <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
        <Bell className="h-4 w-4" />
        <span className="absolute end-2 top-2 h-2 w-2 rounded-full bg-destructive" />
      </Button>

      {/* user menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-md p-1 hover:bg-muted">
            <Avatar>
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="hidden text-start sm:block">
              <div className="text-sm font-medium leading-tight">{user?.username ?? '—'}</div>
              <div className="text-xs text-muted-foreground">{user?.email ?? ''}</div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{t('common.account', 'Account')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/app/profile')}>
            <User className="me-2 h-4 w-4" />
            {t('common.profile', 'Profile')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              logout();
              navigate('/login');
            }}
          >
            <LogOut className="me-2 h-4 w-4" />
            {t('common.logout', 'Sign out')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
